// ─── Workshop Truck Availability Tracker ───
// StatusUpdateModal – Workshop status update form with full validation
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useEffect } from 'react'
import { X, Loader2, Plus, Trash2 } from 'lucide-react'
import type { Truck, WorkshopStatus, WorkshopLocation, SlaStatus, PartCategory, RequiredPart } from '@/types'
import { ALL_STATUSES } from '@/utils/helpers'
import { api } from '@/utils/api'

const PART_CATEGORIES: PartCategory[] = [
  'Transmission', 'Suspension', 'Body parts', 'Engine parts',
  'Fuel system', 'Water system', 'Tires', 'Brake Systems', 'PM Parts', 'Other'
]

const WORKSHOP_LOCATIONS: WorkshopLocation[] = [
  'CALABAR', 'ITORI', 'INTRANSIT', 'OPIC', 'OJOTA', 'SAGAMU'
]

const SLA_OPTIONS: SlaStatus[] = ['SLA', 'NON SLA']

// ─── Props ──────────────────────────────────────────────────

interface StatusUpdateModalProps {
  truck: Truck | null
  onClose: () => void
  onSuccess: () => void
}

// ─── Status emoji/colour indicators ─────────────────────────

const STATUS_INDICATORS: Record<WorkshopStatus, string> = {
  'Active Fleet': '🟢',
  'In Assessment': '🟡',
  'Awaiting Parts': '🔴',
  'Work In Progress': '🟣',
  'Ready for Release': '🔵',
  'Released': '⚫',
}

// ─── Form state type ────────────────────────────────────────

interface FormState {
  workshop_status: WorkshopStatus
  workshop_location: WorkshopLocation | ''
  sla_status: SlaStatus | ''
  required_jobs: string
  workshop_entry_date: string
  estimated_release_date: string
  workshop_notes: string
  updated_by: string
}

interface FormErrors {
  workshop_status?: string
  required_jobs?: string
  estimated_release_date?: string
  updated_by?: string
  submit?: string
}

// ─── Today helper ───────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

// ─── Component ──────────────────────────────────────────────

export default function StatusUpdateModal({
  truck,
  onClose,
  onSuccess,
}: StatusUpdateModalProps) {
  const [form, setForm] = useState<FormState>({
    workshop_status: 'Active Fleet',
    workshop_location: '',
    sla_status: '',
    required_jobs: '',
    workshop_entry_date: '',
    estimated_release_date: '',
    workshop_notes: '',
    updated_by: 'Workshop Supervisor',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Parts state
  const [parts, setParts] = useState<Record<string, RequiredPart[]>>({})
  const [newPartCat, setNewPartCat] = useState<PartCategory>('Engine parts')
  const [newPartDesc, setNewPartDesc] = useState('')
  const [newPartCost, setNewPartCost] = useState('')

  // Pre-fill form from truck prop whenever it changes
  useEffect(() => {
    if (!truck) return
    setForm({
      workshop_status: truck.workshop_status,
      workshop_location: truck.workshop_location ?? '',
      sla_status: truck.sla_status ?? '',
      required_jobs: truck.required_jobs ?? '',
      workshop_entry_date: truck.workshop_entry_date?.split('T')[0] ?? '',
      estimated_release_date: truck.estimated_release_date?.split('T')[0] ?? '',
      workshop_notes: truck.workshop_notes ?? '',
      updated_by: truck.updated_by || 'Workshop Supervisor',
    })

    try {
      setParts(JSON.parse(truck.required_parts || '{}'))
    } catch {
      setParts({})
    }

    setErrors({})
  }, [truck?.asset_id])

  if (!truck) return null

  // ── Field updater ─────────────────────────────────────────
  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    // Clear field error on change
    if (errors[key as keyof FormErrors]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key as keyof FormErrors]
        return next
      })
    }
  }

  // ── Validation ────────────────────────────────────────────
  const validate = (): boolean => {
    const errs: FormErrors = {}

    // Status must be valid
    if (!ALL_STATUSES.includes(form.workshop_status)) {
      errs.workshop_status = 'Please select a valid workshop status'
    }

    // Required jobs mandatory for certain statuses
    if (
      (form.workshop_status === 'Awaiting Parts' ||
        form.workshop_status === 'Work In Progress') &&
      !form.required_jobs.trim()
    ) {
      errs.required_jobs = 'Required jobs must be specified for this status'
    }

    // Estimated release date required for Ready for Release
    if (
      form.workshop_status === 'Ready for Release' &&
      !form.estimated_release_date
    ) {
      errs.estimated_release_date =
        'Estimated release date is required for Ready for Release'
    }

    // Estimated release date must be >= today if set
    if (form.estimated_release_date && form.estimated_release_date < todayISO()) {
      errs.estimated_release_date = 'Release date cannot be in the past'
    }

    // Updated by is required
    if (!form.updated_by.trim()) {
      errs.updated_by = 'Please enter your name or staff ID'
    }

    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  // ── Parts Handlers ────────────────────────────────────────
  const handleAddPart = () => {
    if (!newPartDesc.trim() || !newPartCost) return
    setParts(prev => {
      const catParts = prev[newPartCat] || []
      return {
        ...prev,
        [newPartCat]: [...catParts, { description: newPartDesc.trim(), estimated_cost: Number(newPartCost) }]
      }
    })
    setNewPartDesc('')
    setNewPartCost('')
  }

  const handleRemovePart = (cat: string, index: number) => {
    setParts(prev => {
      const next = { ...prev }
      next[cat] = next[cat].filter((_, i) => i !== index)
      if (next[cat].length === 0) delete next[cat]
      return next
    })
  }

  // ── Submit handler ────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return

    setIsSubmitting(true)
    setErrors((prev) => ({ ...prev, submit: undefined }))

    try {
      await api.patch(`/api/trucks/${truck.asset_id}`, {
        workshop_status: form.workshop_status,
        workshop_location: form.workshop_location || null,
        sla_status: form.sla_status || null,
        required_jobs: form.required_jobs,
        required_parts: JSON.stringify(parts),
        estimated_release_date: form.estimated_release_date || null,
        workshop_entry_date: form.workshop_entry_date || null,
        workshop_notes: form.workshop_notes,
        updated_by: form.updated_by.trim(),
      })

      onSuccess()
      onClose()
    } catch {
      setErrors((prev) => ({
        ...prev,
        submit: 'Update failed. Please try again.',
      }))
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Disable date fields when Active Fleet ─────────────────
  const isActiveFleet = form.workshop_status === 'Active Fleet'

  // ═════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════

  return (
    <div
      className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto z-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ═══ HEADER ════════════════════════════════════════ */}
        <div className="bg-[#1E2A3A] px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">Update Workshop Status</h2>
            <p className="text-gray-300 text-sm">
              {truck.registration} — {truck.client_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* ═══ FORM BODY ═════════════════════════════════════ */}
        <div className="px-6 py-5 space-y-4">
          {/* Submit error banner */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              {errors.submit}
            </div>
          )}

          {/* FIELD 1 — Workshop Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workshop Status <span className="text-red-500">*</span>
            </label>
            <select
              value={form.workshop_status}
              onChange={(e) =>
                updateField('workshop_status', e.target.value as WorkshopStatus)
              }
              disabled={isSubmitting}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-gray-100 disabled:cursor-not-allowed
                         ${errors.workshop_status ? 'border-red-400' : 'border-gray-300'}`}
            >
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_INDICATORS[s]} {s}
                </option>
              ))}
            </select>
            {errors.workshop_status && (
              <p className="text-xs text-red-500 mt-1">{errors.workshop_status}</p>
            )}
          </div>

          {/* FIELD 1.5 — Workshop Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workshop Location
            </label>
            <select
              value={form.workshop_location}
              onChange={(e) =>
                updateField('workshop_location', e.target.value as WorkshopLocation | '')
              }
              disabled={isSubmitting}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">— Select Workshop —</option>
              {WORKSHOP_LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          {/* FIELD 1.6 — SLA Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SLA Status
            </label>
            <select
              value={form.sla_status}
              onChange={(e) =>
                updateField('sla_status', e.target.value as SlaStatus | '')
              }
              disabled={isSubmitting}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">— Select SLA Status —</option>
              {SLA_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* FIELD 2 — Required Jobs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Required Jobs
            </label>
            <textarea
              rows={4}
              value={form.required_jobs}
              onChange={(e) => updateField('required_jobs', e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g. Oil change, Brake pad replacement, Tyre rotation"
              className={`w-full border rounded-lg px-3 py-2.5 text-sm resize-none
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-gray-100 disabled:cursor-not-allowed
                         ${errors.required_jobs ? 'border-red-400' : 'border-gray-300'}`}
            />
            <p className="text-xs text-gray-400 mt-1">
              Separate multiple jobs with commas
            </p>
            {errors.required_jobs && (
              <p className="text-xs text-red-500 mt-1">{errors.required_jobs}</p>
            )}
          </div>

          {/* FIELD 2.5 — Required Parts */}
          <div className="bg-gray-50 p-4 border rounded-xl border-gray-200">
            <h3 className="block text-sm font-semibold text-gray-800 mb-3">
              Required Parts & Estimated Costs
            </h3>

            {/* Current Parts List */}
            {Object.keys(parts).length > 0 && (
              <div className="mb-4 space-y-3">
                {Object.entries(parts).map(([cat, items]) => (
                  <div key={cat} className="text-sm">
                    <span className="font-medium text-[#1E2A3A] text-xs uppercase tracking-wide bg-gray-200 px-2 py-0.5 rounded">{cat}</span>
                    <ul className="mt-1.5 space-y-1">
                      {items.map((item, i) => (
                        <li key={i} className="flex items-center justify-between text-gray-700 bg-white border border-gray-100 px-3 py-1.5 rounded-lg shadow-sm">
                          <span>{item.description}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-green-700 font-medium">₦{item.estimated_cost.toLocaleString()}</span>
                            <button
                              onClick={() => handleRemovePart(cat, i)}
                              className="text-gray-400 hover:text-red-500 p-1 rounded-md"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Add Part Form */}
            <div className="grid grid-cols-12 gap-2 mt-2 items-end">
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs text-gray-500 mb-1">Category</label>
                <select
                  value={newPartCat}
                  onChange={e => setNewPartCat(e.target.value as PartCategory)}
                  className="w-full border rounded-lg px-2.5 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  {PART_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="Part name"
                  value={newPartDesc}
                  onChange={e => setNewPartDesc(e.target.value)}
                  className="w-full border rounded-lg px-2.5 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="col-span-8 sm:col-span-3">
                <label className="block text-xs text-gray-500 mb-1">Est. Cost (₦)</label>
                <input
                  type="number"
                  placeholder="e.g. 50000"
                  value={newPartCost}
                  onChange={e => setNewPartCost(e.target.value)}
                  className="w-full border rounded-lg px-2.5 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="col-span-4 sm:col-span-1 flex justify-end">
                <button
                  onClick={handleAddPart}
                  disabled={!newPartDesc.trim() || !newPartCost}
                  className="w-full h-[38px] flex items-center justify-center bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* FIELD 3 — Workshop Entry Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workshop Entry Date
            </label>
            <input
              type="date"
              value={form.workshop_entry_date}
              onChange={(e) => updateField('workshop_entry_date', e.target.value)}
              disabled={isSubmitting || isActiveFleet}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* FIELD 4 — Estimated Release Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Release Date
            </label>
            <input
              type="date"
              value={form.estimated_release_date}
              onChange={(e) => updateField('estimated_release_date', e.target.value)}
              disabled={isSubmitting || isActiveFleet}
              min={todayISO()}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-gray-100 disabled:cursor-not-allowed
                         ${errors.estimated_release_date ? 'border-red-400' : 'border-gray-300'}`}
            />
            {errors.estimated_release_date && (
              <p className="text-xs text-red-500 mt-1">{errors.estimated_release_date}</p>
            )}
          </div>

          {/* FIELD 5 — Workshop Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workshop Notes
            </label>
            <textarea
              rows={3}
              value={form.workshop_notes}
              onChange={(e) => updateField('workshop_notes', e.target.value)}
              disabled={isSubmitting}
              placeholder="Additional notes for the workshop team..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-none
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* FIELD 6 — Updated By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated By <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.updated_by}
              onChange={(e) => updateField('updated_by', e.target.value)}
              disabled={isSubmitting}
              placeholder="Your name or staff ID"
              className={`w-full border rounded-lg px-3 py-2.5 text-sm
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-gray-100 disabled:cursor-not-allowed
                         ${errors.updated_by ? 'border-red-400' : 'border-gray-300'}`}
            />
            {errors.updated_by && (
              <p className="text-xs text-red-500 mt-1">{errors.updated_by}</p>
            )}
          </div>
        </div>

        {/* ═══ FOOTER ════════════════════════════════════════ */}
        <div className="border-t border-gray-100 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm
                       hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-[#3B82F6] text-white rounded-lg text-sm font-medium
                       hover:bg-blue-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Updating…
              </>
            ) : (
              'Update Status'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
